/**
 * An asset's versions. 1.2.2: "Upload… → A new version of this asset", a resumable upload (POST
 * /api/v1/assets/:id/versions/uploads with VersionUploadBody, then the usual chunks and complete; the tray shows it).
 * When one finishes, the asset, its masters and the Library are refreshed. 1.2.3: the Versions tab (list, Make current, delete
 * to the bin, restore, purge) and Administration → Storage → Old versions (scan, cleanup job).
 */
import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { OldVersionsCriteria, OldVersionsScan, VersionCleanupJobDTO, VersionDTO } from '@prism/contracts';
import { api } from '../client';
import { queryKeys } from '../queryKeys';
import { uploader, useUploads, type UploadItem, type VersionTarget } from '../../upload/uploader';

/** 1.2.3: every version of an asset (the Versions tab). */
export function useAssetVersions(assetId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.assetVersions(assetId),
    queryFn: () => api<{ items: VersionDTO[] }>(`/api/v1/assets/${assetId}/versions`),
    enabled: Boolean(assetId) && enabled,
  });
}

/** Delete an earlier version to the bin, restore or purge it; each refreshes the versions, the asset and the bin. */
export function useVersionActions(assetId: string) {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: queryKeys.assetVersions(assetId) });
    void qc.invalidateQueries({ queryKey: queryKeys.asset(assetId) });
    void qc.invalidateQueries({ queryKey: queryKeys.assetAudit(assetId) });
    void qc.invalidateQueries({ queryKey: queryKeys.recycleBins() });
  };
  return {
    remove: useMutation({
      mutationFn: (versionId: string) => api<{ version: number; recycleBin: boolean; purgeAfter: string }>(`/api/v1/assets/${assetId}/versions/${versionId}/delete`, { method: 'POST', json: {} }),
      onSuccess: refresh,
    }),
    restore: useMutation({ mutationFn: (versionId: string) => api(`/api/v1/recycle-bin/versions/${versionId}/restore`, { method: 'POST', json: {} }), onSuccess: refresh }),
    purge: useMutation({ mutationFn: (versionId: string) => api(`/api/v1/recycle-bin/versions/${versionId}/purge`, { method: 'POST', json: {} }), onSuccess: refresh }),
    /** Make current: an earlier version becomes the current one again; the asset's masters and the Library follow. */
    makeCurrent: useMutation({
      mutationFn: (v: { number: number; expectedCurrent: number }) =>
        api<{ version: number; previousVersion: number }>(`/api/v1/assets/${assetId}/versions/${v.number}/make-current`, { method: 'POST', json: { expectedCurrent: v.expectedCurrent } }),
      onSuccess: () => {
        refresh();
        void qc.invalidateQueries({ queryKey: queryKeys.assetMasters(assetId) });
        void qc.invalidateQueries({ queryKey: queryKeys.assets() });
      },
    }),
  };
}

/** Restore or purge a version from the recycle bin page (the asset is not known there). */
export function useBinVersionActions() {
  const qc = useQueryClient();
  const refresh = (assetId: string) => {
    void qc.invalidateQueries({ queryKey: queryKeys.recycleBins() });
    void qc.invalidateQueries({ queryKey: queryKeys.assetVersions(assetId) });
    void qc.invalidateQueries({ queryKey: queryKeys.asset(assetId) });
  };
  return {
    restore: useMutation({ mutationFn: (v: { versionId: string; assetId: string }) => api(`/api/v1/recycle-bin/versions/${v.versionId}/restore`, { method: 'POST', json: {} }), onSuccess: (_d, v) => refresh(v.assetId) }),
    purge: useMutation({ mutationFn: (v: { versionId: string; assetId: string }) => api(`/api/v1/recycle-bin/versions/${v.versionId}/purge`, { method: 'POST', json: {} }), onSuccess: (_d, v) => refresh(v.assetId) }),
  };
}

/** Administration → Storage → Old versions: the scan, the cleanup job and the latest jobs (polled while one runs). */
export function useOldVersions() {
  const qc = useQueryClient();
  const jobs = useQuery({
    queryKey: queryKeys.oldVersionJobs(),
    queryFn: () => api<{ items: VersionCleanupJobDTO[] }>('/api/v1/storage/old-versions/jobs'),
    refetchInterval: (q) => (q.state.data?.items.some((j) => j.status === 'queued' || j.status === 'running') ? 1500 : false),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: queryKeys.oldVersionJobs() });
  return {
    jobs,
    scan: useMutation({ mutationFn: (criteria: OldVersionsCriteria) => api<OldVersionsScan>('/api/v1/storage/old-versions/scan', { method: 'POST', json: criteria }) }),
    cleanup: useMutation({
      mutationFn: (b: { criteria: OldVersionsCriteria; confirmCount: number }) => api<VersionCleanupJobDTO>('/api/v1/storage/old-versions/cleanup', { method: 'POST', json: b }),
      onSuccess: () => { void refresh(); void qc.invalidateQueries({ queryKey: queryKeys.recycleBins() }); },
    }),
    cancel: useMutation({ mutationFn: (id: string) => api<VersionCleanupJobDTO>(`/api/v1/storage/old-versions/jobs/${id}/cancel`, { method: 'POST', json: {} }), onSuccess: () => void refresh() }),
  };
}

export function useVersionUpload(assetId: string) {
  const qc = useQueryClient();
  const uploads = useUploads().filter((u) => u.version?.assetId === assetId);
  const done = uploads.filter((u) => u.state === 'done').length;
  const seen = useRef(done);
  useEffect(() => {
    if (done === seen.current) return;
    seen.current = done;
    void qc.invalidateQueries({ queryKey: queryKeys.asset(assetId) });
    void qc.invalidateQueries({ queryKey: queryKeys.assetMasters(assetId) });
    void qc.invalidateQueries({ queryKey: queryKeys.assetVersions(assetId) });
    void qc.invalidateQueries({ queryKey: queryKeys.assets() });
  }, [done, assetId, qc]);
  return {
    add: (file: File, target: Omit<VersionTarget, 'assetId'>) => uploader.add([file], { version: { ...target, assetId } }),
    inFlight: uploads.filter((u): u is UploadItem => !['done', 'cancelled', 'error', 'duplicate'].includes(u.state)),
  };
}
